import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'validator';
import { useDispatch } from 'react-redux';

import { validate } from '../../../utils/validation';
import { dispatchAsync } from '../../../store/storeModule';
import { showSnackbar, patchAccount } from '../../../store/action-creators';

import useInput from '../../../hooks/useInput';
import useValidation from '../../../hooks/useValidation';

import StdModal from '../../atoms/StdModal';
import StdTextField from '../../atoms/StdTextField';
import CustomIcon from '../../atoms/CustomIcon';
import ModalHeader from '../../atoms/ModalHeader';
import StdButton from '../../atoms/StdButton';

interface IProps extends IModalBase {
    editedPhone?: string;
    onSubmit?: ICallback;
}

interface IForm {
    form: {
        phone: string;
    };
    errors: IErrorHash;
}

const initForm: IForm = {
    form: {
        phone: ''
    },
    errors: {}
};

const EditPhoneModal: React.FC<IProps> = ({
    editedPhone,
    open,
    onClose,
    onSubmit
}) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [input, setInput] = useState(initForm);

    useEffect(() => {
        if (editedPhone) {
            setInput((s) => ({
                ...s,
                form: {
                    ...s.form,
                    phone: editedPhone
                }
            }));
        }
    }, [editedPhone]);

    const handleInput = useInput(setInput);

    const validateFields = useValidation(input, setInput, (hash) => {
        const { phone } = input.form;
        return [validate({ phone }, !isEmpty(phone), hash)].every(Boolean);
    });

    const makeRequest = () => {
        if (validateFields()) {
            const { phone } = input.form;
            dispatchAsync(dispatch, patchAccount({ mobile_number: phone }))
                .then(onSubmit)
                .catch(({ error }) =>
                    dispatch(
                        showSnackbar({ message: 'edit_phone_error', error })
                    )
                );
            onClose();
        }
    };

    const { form, errors } = input;
    return (
        <StdModal open={open} onClose={onClose} small>
            <ModalHeader
                leftNode={
                    <span className="std-space-nowrap">{t('edit_phone')}</span>
                }
                rightNode={<CustomIcon name="cross" onClick={onClose} ripple />}
            />
            <div className="modal__section std-pt">
                <StdTextField
                    className="inline-m"
                    label={t('phone')}
                    value={form.phone}
                    name="phone"
                    error={errors['phone']}
                    width="100%"
                    onChange={handleInput}
                />
                <div className="button-wrapper std-flex-between std-mt">
                    <StdButton width="120px" onClick={onClose}>
                        {t('cancel')}
                    </StdButton>
                    <StdButton
                        width={'120px'}
                        type="primary"
                        onClick={makeRequest}
                    >
                        {t('save')}
                    </StdButton>
                </div>
            </div>
        </StdModal>
    );
};

export default EditPhoneModal;
